import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useLocation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { useHydrated } from 'remix-utils/use-hydrated'
import { wishlistActionI18next } from '#app/components/common/wishlist-action.tsx'
import {
	ADD_TO_WISHLIST_FORM,
	ADD_TO_WISHLIST_INPUTS,
	addToWishlistSchema,
} from '#app/components/forms/add-to-wishlist-form/add-to-wishlist-form.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseAddToWishlistProps = {
	product: GetProduct
}

function useAddToWishlistForm({ product }: UseAddToWishlistProps) {
	const { t } = useTranslation(wishlistActionI18next.uiWishlistAction)
	const isHydrated = useHydrated()
	const location = useLocation()

	const fetcherKey = ADD_TO_WISHLIST_FORM.generateFetcherKey!({
		id: product.slug!,
	})
	const fetcher = useFetcher({
		key: fetcherKey,
	})

	const formValues = {
		[ROOT_INPUTS.subaction]: ADD_TO_WISHLIST_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(!isHydrated),
		[ROOT_INPUTS.redirect]: location.pathname,
	}
	const [form, fields] = useForm({
		defaultValue: {
			[ADD_TO_WISHLIST_INPUTS.productId]: product.id,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: addToWishlistSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

	return {
		addToWishlistFetcherKey: fetcherKey,
		addToWishlistFetcher: fetcher,
		addToWishlistFormValues: formValues,
		addToWishlistForm: form,
		addToWishlistFields: fields,
		t,
	}
}

export { type UseAddToWishlistProps, useAddToWishlistForm }
